'use client'

import { useEffect, useState } from 'react'
import { getFileUrl } from '@/lib/utils/file-url'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getPrescription,
    deletePrescription,
    Prescription,
} from '@/lib/api/prescriptions/prescription-api'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowBack,
    MdDescription,
    MdEdit,
    MdDelete,
    MdPerson,
    MdCalendarToday,
    MdMedicalServices,
    MdPrint,
    MdMedication,
    MdDraw,
    MdVerified,
    MdDownload,
    MdOpenInNew,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'

export default function PrescriptionDetailPage() {
    const navigate = useNavigate()
    const { canUpdate, canDelete } = useResourcePermissions('medical', 'prescriptions')
    const { id } = useParams<{ id: string }>()
    const [prescription, setPrescription] = useState<Prescription | null>(null)
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showSignModal, setShowSignModal] = useState(false)

    useEffect(() => {
        if (id) {
            loadPrescription()
        }
    }, [id])

    const loadPrescription = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getPrescription(parseInt(id, 10))
            if (response.success && response.result) {
                setPrescription(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar receita')
                navigate('/medical/prescriptions')
            }
        } catch (error) {
            toast.error('Erro ao carregar receita')
            navigate('/medical/prescriptions')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!id || !prescription) return

        setDeleting(true)
        try {
            const response = await deletePrescription(parseInt(id, 10))
            if (response.success) {
                toast.success('Receita excluída com sucesso!')
                navigate('/medical/prescriptions')
            } else {
                toast.error(response.error || 'Erro ao excluir receita')
            }
        } catch (error) {
            toast.error('Erro ao excluir receita')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const handlePrint = () => {
        window.print()
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes da receita...
                </p>
            </div>
        )
    }

    if (!prescription) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdDescription className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Receita não encontrada
                    </h3>
                    <p className="text-sm text-base-content/60">
                        A receita que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate('/medical/prescriptions')}
                    className="btn btn-secondary"
                >
                    Voltar para Receitas
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between print:hidden">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/medical/prescriptions')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                            <MdDescription className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Receita #{prescription.id}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Detalhes da receita
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrint}
                        className="btn btn-ghost gap-2"
                    >
                        <MdPrint className="text-lg" />
                    </button>
                    {canUpdate && !prescription.signedAt && (
                        <button
                            onClick={() => setShowSignModal(true)}
                            className="btn btn-success gap-2"
                        >
                            <MdDraw className="text-lg" />
                            <span className="hidden sm:inline">Assinar Digitalmente</span>
                        </button>
                    )}
                    {prescription.signedPdfUrl && (
                        <a
                            href={getFileUrl(prescription.signedPdfUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-ghost gap-2"
                            title="Baixar PDF assinado"
                        >
                            <MdDownload className="text-lg" />
                        </a>
                    )}
                    {canUpdate && (
                        <button
                            onClick={() =>
                                navigate(`/medical/prescriptions/edit/${prescription.id}`)
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdEdit className="text-lg" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="btn btn-error btn-outline gap-2"
                        >
                            {deleting ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdDelete className="text-lg" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <MdCalendarToday />
                                <span>
                                    {format(
                                        new Date(prescription.prescriptionDate),
                                        "dd 'de' MMMM 'de' yyyy",
                                        { locale: ptBR }
                                    )}
                                </span>
                            </div>
                            {prescription.signedAt && (
                                <span className="badge badge-success gap-1">
                                    <MdVerified className="text-sm" />
                                    Assinado
                                </span>
                            )}
                        </div>
                        {prescription.validUntil && (
                            <span className="badge badge-info">
                                Válida até{' '}
                                {format(
                                    new Date(prescription.validUntil),
                                    'dd/MM/yyyy',
                                    { locale: ptBR }
                                )}
                            </span>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {prescription.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Paciente
                                    </p>
                                    <p className="font-semibold">
                                        {prescription.client.name}
                                    </p>
                                </div>
                            </div>
                        )}

                        {prescription.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Profissional
                                    </p>
                                    <p className="font-semibold">
                                        {prescription.professional.name}
                                    </p>
                                    {prescription.professional
                                        .registrationNumber && (
                                            <p className="text-sm text-base-content/60">
                                                {prescription.professional.councilName}{' '}
                                                {
                                                    prescription.professional
                                                        .registrationNumber
                                                }
                                            </p>
                                        )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mb-6">
                        <h3 className="text-lg font-bold text-base-content mb-3">
                            Prescrição
                        </h3>
                        <div className="p-4 bg-base-200/50 rounded-xl">
                            <p className="text-base-content whitespace-pre-wrap">
                                {prescription.content}
                            </p>
                        </div>
                    </div>

                    {prescription.observations && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">
                                Observações
                            </h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">
                                    {prescription.observations}
                                </p>
                            </div>
                        </div>
                    )}

                    {prescription.signedAt && (
                        <div className="mt-6 pt-6 border-t border-base-300/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <MdVerified className="text-2xl text-success" />
                                    <div>
                                        <p className="font-semibold text-success">
                                            Documento Assinado Digitalmente
                                        </p>
                                        <p className="text-sm text-base-content/60">
                                            Assinado em{' '}
                                            {format(
                                                new Date(prescription.signedAt),
                                                "dd/MM/yyyy 'às' HH:mm",
                                                { locale: ptBR }
                                            )}
                                        </p>
                                    </div>
                                </div>
                                {prescription.signatureData &&
                                    prescription.signatureData?.signatureLogId && (
                                    <a
                                        href={`/verify/${prescription.signatureData?.signatureLogId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-ghost btn-sm gap-1"
                                    >
                                        <MdOpenInNew className="text-sm" />
                                        Verificar
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {prescription.items && prescription.items.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdMedication className="text-xl text-green-500" />
                            <h3 className="text-lg font-bold text-base-content">
                                Medicamentos
                            </h3>
                            <span className="badge badge-secondary badge-sm">
                                {prescription.items.length}
                            </span>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {prescription.items.map((item, index) => (
                            <div
                                key={item.id || index}
                                className="bg-base-200/50 rounded-xl border border-base-300/50 p-4"
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <h4 className="font-bold text-base-content text-lg">
                                        {item.medication}
                                    </h4>
                                    {item.quantity && (
                                        <span className="badge badge-ghost">
                                            Qtd: {item.quantity}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {item.dosage && (
                                        <div>
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Dosagem
                                            </p>
                                            <p className="font-medium">
                                                {item.dosage}
                                            </p>
                                        </div>
                                    )}

                                    {item.frequency && (
                                        <div>
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Frequência
                                            </p>
                                            <p className="font-medium">
                                                {item.frequency}
                                            </p>
                                        </div>
                                    )}

                                    {item.duration && (
                                        <div>
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Duração
                                            </p>
                                            <p className="font-medium">
                                                {item.duration}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {item.instructions && (
                                    <div className="mt-3 pt-3 border-t border-base-300/50">
                                        <p className="text-xs text-base-content/60 mb-1">
                                            Instruções
                                        </p>
                                        <p className="text-base-content/80">
                                            {item.instructions}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir receita"
                message="Tem certeza que deseja excluir esta receita?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <SignDocumentModal
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                documentType="PRESCRIPTION"
                documentId={prescription.id}
                documentTitle={`Receita #${prescription.id}`}
                onSigned={() => loadPrescription()}
            />
        </div>
    )
}
